import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ShieldCheck, Check, Users as UsersIcon, ArrowLeft, Info, Search, Filter, Printer,
} from "lucide-react";
import BBSLogo from "@/components/BBSLogo";

/**
 * Admin-only reference page — a live role × permission matrix.
 * Data is fetched from GET /api/roles/matrix which is derived from the
 * *_ROLES sets in server.py, so this page automatically stays in sync as
 * permissions evolve.
 */
export default function RolesPermissions() {
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/roles/matrix");
        setMatrix(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not load matrix");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!matrix) return [];
    const needle = q.trim().toLowerCase();
    return matrix.permissions.filter((p) => {
      if (needle && !`${p.label} ${p.description} ${p.group}`.toLowerCase().includes(needle)) return false;
      if (roleFilter !== "all" && !p.allowed_roles.includes(roleFilter)) return false;
      return true;
    });
  }, [matrix, q, roleFilter]);

  const grouped = useMemo(() => {
    const map = {};
    for (const p of filtered) {
      const g = p.group || "General";
      if (!map[g]) map[g] = [];
      map[g].push(p);
    }
    return map;
  }, [filtered]);

  if (loading) {
    return (
      <div className="p-8 text-slate-400">Loading role matrix…</div>
    );
  }
  if (!matrix) {
    return (
      <div className="p-8">
        <div className="max-w-md p-6 border border-red-200 rounded-lg bg-red-50 text-red-800">
          Could not load the role matrix.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6 print:p-0 print:space-y-4" data-testid="roles-permissions-page">
      {/* Print-only header — full BBS branding */}
      <div className="hidden print:block pb-4 border-b-2 border-[#7C1D3A]">
        <div className="flex items-start justify-between">
          <div>
            <BBSLogo size={44} />
            <div className="mt-1 text-[9px] font-bold tracking-[0.3em] text-slate-700 uppercase">
              Access Control Reference · Internal Document
            </div>
          </div>
          <div className="text-right">
            <div className="font-heading text-2xl font-bold text-[#7C1D3A] leading-none">Asset IQ</div>
            <div className="text-[10px] text-slate-500 mt-1">Generated {new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" })}</div>
          </div>
        </div>
      </div>

      {/* Header — hidden on print (print header above replaces it) */}
      <div className="flex items-start justify-between flex-wrap gap-3 print:hidden">
        <div>
          <Link to="/users" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-1">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to User Management
          </Link>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#fcf0f3] text-[#7C1D3A] text-[10px] font-semibold tracking-widest uppercase mt-1">
            <ShieldCheck className="w-3 h-3" /> Access Control Reference
          </div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-2">Roles &amp; Permissions</h1>
          <p className="text-sm text-slate-600 mt-1">
            Live matrix — every checkmark comes directly from the backend&apos;s role gates, so it always reflects the app&apos;s current behaviour.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            asChild
            variant="outline"
            className="gap-2 print:hidden"
            data-testid="edit-matrix-btn"
          >
            <Link to="/roles-permissions/edit">
              <ShieldCheck className="w-4 h-4" /> Edit matrix
            </Link>
          </Button>
          <Button
            onClick={() => window.print()}
            className="bg-[#7C1D3A] hover:bg-[#6c1c36] gap-2"
            data-testid="print-matrix-btn"
          >
            <Printer className="w-4 h-4" /> Print / Save as PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center print:hidden">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search permissions…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            data-testid="perm-search"
          />
        </div>
        <div className="flex items-center gap-1.5 text-xs text-slate-500 whitespace-nowrap">
          <Filter className="w-3.5 h-3.5" /> Only show permissions granted to:
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="h-9 rounded-md border border-slate-200 px-2 text-sm"
          data-testid="perm-role-filter"
        >
          <option value="all">Any role</option>
          {matrix.roles.map((r) => (
            <option key={r.key} value={r.key}>{r.label}</option>
          ))}
        </select>
      </div>

      {/* Role legend cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {matrix.roles.map((r) => (
          <div key={r.key} className="p-3 rounded-lg border border-slate-200 bg-white">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-[#fcf0f3] text-[#7C1D3A] flex items-center justify-center">
                <UsersIcon className="w-3.5 h-3.5" />
              </div>
              <div className="font-heading font-semibold text-sm text-slate-900 truncate">{r.label}</div>
            </div>
            <div className="text-[11px] text-slate-500 mt-1.5 leading-snug">{r.description}</div>
          </div>
        ))}
      </div>

      {/* Matrix — one table per group */}
      {Object.keys(grouped).length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-slate-400">
            No permissions match the current filter.
          </CardContent>
        </Card>
      ) : (
        Object.entries(grouped).map(([groupName, perms]) => (
          <Card key={groupName} data-testid={`perm-group-${groupName.toLowerCase()}`}>
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg text-slate-900">{groupName}</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left font-semibold text-slate-600 py-2 px-2 min-w-[220px]">Permission</th>
                    {matrix.roles.map((r) => (
                      <th key={r.key} className="font-semibold text-slate-600 py-2 px-1.5 text-center min-w-[80px]">
                        <div className="whitespace-nowrap text-[11px]">{r.label}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {perms.map((p) => (
                    <tr key={p.key} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`perm-row-${p.key}`}>
                      <td className="py-2.5 px-2 align-top">
                        <div className="font-medium text-slate-900">{p.label}</div>
                        <div className="text-xs text-slate-500 mt-0.5 max-w-[280px]">{p.description}</div>
                      </td>
                      {matrix.roles.map((r) => (
                        <td key={r.key} className="text-center py-2.5 px-1.5 align-top">
                          {p.allowed_roles.includes(r.key) ? (
                            <Check className="w-4 h-4 mx-auto text-emerald-600" />
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        ))
      )}

      <div className="text-xs text-slate-400 flex items-start gap-2 p-3 rounded-md bg-slate-50 border border-slate-200 print:hidden">
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span>
          Sidebar visibility follows the same rules. To change any of these permissions, ask your engineer to edit the <code className="font-mono text-[10px] bg-white px-1 py-0.5 rounded border">*_ROLES</code> sets in <code className="font-mono text-[10px] bg-white px-1 py-0.5 rounded border">/app/backend/server.py</code>.
        </span>
      </div>

      {/* Print-only footer */}
      <div className="hidden print:block pt-3 border-t border-slate-200 text-[9px] text-slate-500 mt-4">
        © BBS Global Pvt. Ltd. · Asset IQ · Confidential — Internal Use Only. This matrix reflects the live application configuration at the time of generation.
      </div>

      {/* Print-specific tweaks */}
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 10mm; }
          body { background: white !important; }
          /* Compact table rows for printing */
          table { font-size: 9pt; }
          th, td { padding: 4px 6px !important; }
          /* Avoid card shadows / rounded borders looking heavy on paper */
          .print\\:no-shadow { box-shadow: none !important; }
          /* Keep groups together */
          [data-testid^="perm-group-"] { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}
